import config from "appConfig";

const theory = `<h3 class=likesectionHead>Introduction</h3>
<p>This section recalls some basics of electrical engineering [1, 2]. Real-life audio signals are rich signals that include a large number of frequencies. These signals may or may not be periodic meaning that their content may be stationary or may vary over time. However, when a Fourier analysis is performed, any signal can be treated as a superposition of sinusoidal signals. This means that if we know how to measure or model a linear system for each frequency, we will be able to know its response for any complex signal. This approach is also called the harmonic analysis and will be used to study the electroacoustic systems presented in this course. In the second part of this Lesson, electrical components and the laws of electrical network resolution will be presented. This knowledge will be used extensively in this course, for example to model electroacoustic sensors or actuators, or to design analog crossovers in a multichannel loudspeaker system.<br class=newline>
<h3 class=likesectionHead>Sinusoidal signal</h3>
<p>
   A sinusoidal signal is represented in <a href=# data-img-alt="Figure 1. Sinusoidal signal." data-img-src=${config.ASSETS_URL}/04/02/theory/figure-1.svg>Figure 1</a>. It oscillates between two extreme values:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>+</mo>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>. The period
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   of the oscillations represents the duration of the repeating sequence; it is the inverse of the frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>T</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>
   which represents the number of cycles per second.
   <figure class=figure>
<p><img alt="Figure 1. Sinusoidal signal." src=${config.ASSETS_URL}/04/02/theory/figure-1.svg><figcaption class=caption>Figure 1. Sinusoidal signal.</figcaption></figure>
<p>The sinusoidal signal can be expressed using the following equation
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>s</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mrow>
                <msub>
                    <mrow>
                        <mi>S</mi>
                    </mrow>
                    <mrow>
                        <mi>m</mi>
                    </mrow>
                </msub>
                <mi class=qopname>cos</mi>
                <mo>(</mo>
                <msub>
                    <mrow> <mn>ω</mn></mrow>
                   <mrow><mn>0</mn></mrow>
                </msub>
                <mi>t</mi>
                <mo>+</mo>
                <msub>
                <mrow>
                    <mi>φ</mi>
                </mrow>
                <mrow>
                    <mn>0</mn>
                </mrow>
                </msub>
             <mo>)</mo>
             </mrow>            
         </math>
      <td class=eq-no>(1)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ω</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>2</mn>
      <mi>π</mi>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   is the pulsation or angular frequency and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>φ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   is the phase of the signal. Note that the value of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>s</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   at
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>t</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   is simply given by:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>s</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <msub>
         <mrow>
            <mi>φ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>.<br class=newline>As the average value of a sinusoidal signal is null, Root Mean Square (RMS) values are used. The RMS value of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>s</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   is given by
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>RMS</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>T</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
                  <msubsup>
                     <mrow>
                        <mo>∫</mo>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>T</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                     </mrow>
                  </msubsup>
                  <msup>
                     <mrow>
                        <mi>s</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mo class=MathClass-open>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
                  <mi mathvariant=italic>dt</mi>
               </mrow>
            </msqrt>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>∕</mo>
            <msqrt>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msqrt>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   Note that the
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
      <mo class=MathClass-bin>∕</mo>
      <msqrt>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msqrt>
   </math>
   factor applies specifically to sine signals.<br class=newline>The RMS value also called effective value is interesting because the energy of systems is often calculated from the square of physical quantities, for example the energy dissipated by Joule effect in a resistor of value
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
   </math>
   is equal to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
      <msubsup>
         <mrow>
            <mi>I</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>RMS</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msubsup>
   </math>. Note that the RMS value of the Alternating Current (AC) is equal to the value of the Direct Current (DC) that would cause the same energy dissipation.
<p>
   We can also write
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>s</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   using a complex exponential, then we get
<table class=equation>
   <tr>
      <td style="font-size: 12px;">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mover accent=true>
               <mrow>
                  <mi>s</mi>
               </mrow>
               <mo accent=true>~</mo>
            </mover>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi>j</mi>
                  <mo class=MathClass-open>(</mo>
                  <msub>
                     <mrow>
                        <mi>ω</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>t</mi>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>φ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </msup>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>[</mo>
            <mi class=qopname>cos</mi>
                <mo>(</mo>
                <msub>
                    <mrow> <mn>ω</mn></mrow>
                   <mrow><mn>0</mn></mrow>
                </msub>
                <mi>t</mi>
                <mo>+</mo>
                <msub>
                <mrow>
                    <mi>φ</mi>
                </mrow>
                <mrow>
                    <mn>0</mn>
                </mrow>
                </msub>
             <mo>)</mo>
             <mo>+</mo>
             <mi>j</mi>
             <mo>sin</mo>
                <mo>(</mo>
                <msub>
                    <mrow> <mn>ω</mn></mrow>
                   <mrow><mn>0</mn></mrow>
                </msub>
                <mi>t</mi>
                <mo>+</mo>
                <msub>
                <mrow>
                    <mi>φ</mi>
                </mrow>
                <mrow>
                    <mn>0</mn>
                </mrow>
                </msub>
             <mo>)</mo>
               <mo class=MathClass-close>]</mo>
            </mo>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>j</mi>
   </math>
   is the imaginary unit. The physical signal
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>s</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   can be obtained by taking the real part of the complex signal:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>s</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi class=qopname>Re</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mover accent=true>
         <mrow>
            <mi>s</mi>
         </mrow>
         <mo accent=true>~</mo>
      </mover>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-close>)</mo>
   </math>. Complex notation offers interesting computational benefits, such as the ability to solve differential equations in a simple way and thus to know, for example, the frequency response of electrical, mechanical or acoustic systems.
<p>Any rich signal, <em><span class=cmti-10>i.e.</span></em> composed of several frequencies, such as a musical or vocal signal, can be decomposed into a sum of complex exponentials. The decomposition process is called Fourier Transform and is defined as
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>S</mi>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msubsup>
               <mrow>
                  <mo>∫</mo>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi>∞</mi>
               </mrow>
               <mrow>
                  <mi>∞</mi>
               </mrow>
            </msubsup>
            <mi>s</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jωt</mi>
               </mrow>
            </msup>
            <mi mathvariant=italic>dt</mi>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(4)
</table>
<p>which can be written in a short form as
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>s</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <munderover>
               <mo class=MathClass-rel>→</mo>
               <mrow>
                <mi></mi>
               </mrow>
               <mrow>
                  <mi mathvariant=bold-script>ℱ</mi>
               </mrow>
            </munderover>
            <mi>S</mi>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   The interest of using Fourier Transform takes on its full meaning when physical laws include derivation or integration operations. In these cases, it is necessary to solve differential equations, which, even if they do not constitute an obstacle, do not quickly give an explicit solution. For instance, if we consider a derivation or primitive integration with respect to time applied to any signal
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>s</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   its Fourier Transform is
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mfrac>
               <mrow>
                  <mi>d</mi>
                  <mi>s</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>dt</mi>
               </mrow>
            </mfrac>
            <munderover>
               <mo class=MathClass-rel>→</mo>
               <mrow>
                  <mi mathvariant=bold-script></mi>
               </mrow>
               <mrow>
                  <mi mathvariant=bold-script>ℱ</mi>
               </mrow>
            </munderover>
            <mi mathvariant=italic>jωS</mi>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(6)
</table>
<p>and
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mo>∫</mo>
            <mi>s</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mi mathvariant=italic>dt</mi>
            <munderover>
               <mo class=MathClass-rel>→</mo>
               <mrow>
                  <mi mathvariant=bold-script></mi>
               </mrow>
               <mrow>
                  <mi mathvariant=bold-script>ℱ</mi>
               </mrow>
            </munderover>
            <mfrac>
               <mrow>
                  <mi>S</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jω</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(7)
</table>
<p>
   These two mathematical operations become multiplication and division by a factor
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>jω</mi>
   </math>, yielding to an easy solving of the steady-state behaviour of many physical systems. The Fourier transform is also particularly useful to determine the frequency response of systems. For example, it will allow us to calculate the frequency response of microphones, speakers, crossover filters, etc.
<h3 class=likesectionHead>One-port elements</h3>
<p>
   One-port elements are electrical components that have two terminals. Examples of ideal active and passive one-port elements are in <a href=# data-img-alt="Figure 2. Examples of one-port element symbols. Active elements are on the upper row: voltage source, current source and sinusoidal voltage source. Passive elements are on the lower row: resistor, pure inductor and capacitor." data-img-src=${config.ASSETS_URL}/04/02/theory/figure-2.svg>Figure 2</a>. Note that in the case of generators, the direction of the current is outwards at the positive voltage pole, whereas in the case of passive components, the direction of the current is defined inwards at the positive terminal.<br class=newline>
   <figure class=figure>
<p><img alt="Figure 2. Examples of one-port element symbols. Active elements are on the upper row: voltage source, current source and sinusoidal voltage source. Passive elements are on the lower row: resistor, pure inductor and capacitor." src=${config.ASSETS_URL}/04/02/theory/figure-2.svg><figcaption class=caption>Figure 2. Examples of one-port element symbols. Active elements are on the upper row: voltage source, current source and sinusoidal voltage source. Passive elements are on the lower row: resistor, pure inductor and capacitor.</figcaption></figure>
<p>
   When using complex notation of signals, Ohm’s law can be generalized to the passive elements of <a href=# data-img-alt="Figure 2. Examples of one-port element symbols. Active elements are on the upper row: voltage source, current source and sinusoidal voltage source. Passive elements are on the lower row: resistor, pure inductor and capacitor." data-img-src=${config.ASSETS_URL}/04/02/theory/figure-2.svg>Figure 2</a> giving the following equation expressing the voltage
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>U</mi>
   </math>
   across the considered one-port element as a function of the current
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>I</mi>
   </math>
   flowing through it
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>U</mi>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>Z</mi>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mi>I</mi>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(8)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Z</mi>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   is the complex electrical impedance. Its value is given in <a href=# data-img-alt="Table 1. Impedance of the classical passive one port elements" data-img-src=${config.ASSETS_URL}/04/02/theory/table-1.svg>Table 1</a> for a resistor, an inductor, and a capacitor. Note that the impedance represents the opposition of a system to an excitation. For example, an electrical circuit can be excited by applying a voltage. The systems response will be a current. The impedance tells us how much current we’re getting for the voltage. This also applies to other domains, for example mechanical systems are excited by a force and respond with a velocity related to their mechanical impedance.
<div class=table>
   <figure class=float>
      <figcaption class=caption>Table 1. Impedance of the classical passive one port elements.</figcaption>
      <div class=tabular>
         <img alt="Table 1. Impedance of the classical passive one port elements." src=${config.ASSETS_URL}/04/02/theory/table-1.svg />
      </div>
   </figure>
</div>
<figure class=figure>
   <p>
      <img alt="Figure 3. Components connected in series." src=${config.ASSETS_URL}/04/02/theory/figure-3.svg>
      <figcaption class=caption>Figure 3. Components connected in series.</figcaption>
</figure>
<p>
   When dealing with a combination of electrical impedances, some simple rules can be used to get the equivalent impedance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>eq</mi>
         </mrow>
      </msub>
   </math>
   of the network. For the series circuit depicted in <a href=# data-img-alt="Figure 3. Components connected in series." data-img-src=${config.ASSETS_URL}/04/02/theory/figure-3.svg>Figure 3</a>, we get
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>eq</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mn>3</mn>
         </mrow>
      </msub>
   </math>. This calculation can be generalized to an association in series of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>N</mi>
   </math>
   electrical impedances
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>eq</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <munderover accent=false accentunder=false>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>n</mi>
                  <mo class=MathClass-rel>=</mo>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>N</mi>
               </mrow>
            </munderover>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(9)
</table>
<p>For the parallel combination of <a href=# data-img-alt="Figure 4. Components connected in parallel." data-img-src=${config.ASSETS_URL}/04/02/theory/figure-4.svg>Figure 4</a>, we get
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>eq</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(10)
</table>
<p>
   that can be generalized to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>N</mi>
   </math>
   electrical impedances with
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>eq</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <munderover accent=false accentunder=false>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>n</mi>
                  <mo class=MathClass-rel>=</mo>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>N</mi>
               </mrow>
            </munderover>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>n</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(11)
</table>
<figure class=figure>
   <p>
      <img alt="Figure 4. Components connected in parallel." src=${config.ASSETS_URL}/04/02/theory/figure-4.svg>
      <figcaption class=caption>Figure 4. Components connected in parallel.</figcaption>
</figure>
<p>
   The power
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=bold-script>𝒫</mi>
   </math>
   absorbed by a complex load
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
      </msub>
   </math>
   supplied by a voltage
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>U</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>jωt</mi>
         </mrow>
      </msup>
   </math>
   is equal to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=bold-script>𝒫</mi>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>U</mi>
            <msup>
               <mrow>
                  <mi>I</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>∗</mo>
               </mrow>
            </msup>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </mfrac>
   </math>, where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>∗</mo>
   </math>
   denotes the complex conjugate.
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=bold-script>𝒫</mi>
   </math>
   is the apparent power and corresponds to the product of the RMS values of voltage and current. The active power
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi mathvariant=bold-script>𝒫</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi class=qopname>Re</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=bold-script>𝒫</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   corresponds to the power dissipated in the load while the reactive power
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi mathvariant=bold-script>𝒫</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi class=qopname>Im</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=bold-script>𝒫</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   corresponds to the power which flows back and forth in the load. Depending on the load type, we get:
<ul class=itemize1>
   <li class=itemize>
      for a pure resistor
      <span style="font-size: 12px;">
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>R</mi>
      </math>,
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi mathvariant=bold-script>𝒫</mi>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi mathvariant=bold-script>𝒫</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <msubsup>
                  <mrow>
                     <mi>U</mi>
                  </mrow>
                  <mrow>
                     <mi>m</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msubsup>
            </mrow>
            <mrow>
               <mn>2</mn>
               <mi>R</mi>
            </mrow>
         </mfrac>
         <mo class=MathClass-rel>=</mo>
         <msubsup>
            <mrow>
               <mi>U</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>RMS</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msubsup>
         <mo class=MathClass-bin>∕</mo>
         <mi>R</mi>
         <mo class=MathClass-rel>=</mo>
         <mi>R</mi>
         <msubsup>
            <mrow>
               <mi>I</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>RMS</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msubsup>
      </math>
      and
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi mathvariant=bold-script>𝒫</mi>
            </mrow>
            <mrow>
               <mi>r</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mn>0</mn>
      </math></span>,
   <li class=itemize>
      for a pure inductor, which coverts energy in a magnetic field, or a capacitor, which stores electrical energy by accumulating electric charges, we have:
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi mathvariant=bold-script>𝒫</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mn>0</mn>
      </math>
      and
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi mathvariant=bold-script>𝒫</mi>
            </mrow>
            <mrow>
               <mi>r</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mi mathvariant=bold-script>𝒫</mi>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <msubsup>
                  <mrow>
                     <mi>U</mi>
                  </mrow>
                  <mrow>
                     <mi mathvariant=italic>RMS</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msubsup>
            </mrow>
            <mrow>
               <mi mathvariant=italic>Lω</mi>
            </mrow>
         </mfrac>
      </math>
      or
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi mathvariant=bold-script>𝒫</mi>
            </mrow>
            <mrow>
               <mi>r</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mi mathvariant=italic>Cω</mi>
         <msubsup>
            <mrow>
               <mi>U</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>RMS</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msubsup>
      </math>,
   <li class=itemize>
      and more generally, for a load with a phase of voltage relative to current equal to
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>φ</mi>
      </math>, we get
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi mathvariant=bold-script>𝒫</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msubsup>
            <mrow>
               <mi>U</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>RMS</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msubsup>
         <mi class=qopname>cos</mi>
         <mo>⁡</mo>
         <mi>φ</mi>
         <mo class=MathClass-bin>∕</mo>
         <mo class=MathClass-rel>|</mo>
         <mi>Z</mi>
         <mo class=MathClass-rel>|</mo>
      </math>
      and
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi mathvariant=bold-script>𝒫</mi>
            </mrow>
            <mrow>
               <mi>r</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msubsup>
            <mrow>
               <mi>U</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>RMS</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msubsup>
         <mi class=qopname>sin</mi>
         <mo>⁡</mo>
         <mi>φ</mi>
         <mo class=MathClass-bin>∕</mo>
         <mo class=MathClass-rel>|</mo>
         <mi>Z</mi>
         <mo class=MathClass-rel>|</mo>
      </math>.
</ul>
<h3 class=likesectionHead>Two-port networks</h3>
<p>
   Two-port networks are electrical circuits that have four terminals. They will be used in this course to model the behaviour of audio transducers, filters, etc., as two-port elements can be built by assembling one-port elements into a network. Classical two-port element arrangements are given in <a href=# data-img-alt="Figure 5. Classical two-port network topologies." data-img-src=${config.ASSETS_URL}/04/02/theory/figure-5.svg>Figure  5</a>.<br class=newline>
   <figure class=figure>
<p><img alt="Figure 5. Classical two-port network topologies." src=${config.ASSETS_URL}/04/02/theory/figure-5.svg><figcaption class=caption>Figure 5. Classical two-port network topologies.</figcaption></figure>
<p>For modelling the transduction processes of electroacoustic systems, two specific types of two-ports will be used: the ideal transformer and the gyrator [3] (see <a href=# data-img-alt="Figure 6. Upper part: transformer symbol, lower part: gyrator symbol." data-img-src=${config.ASSETS_URL}/04/02/theory/figure-6.svg>Figure 6</a>).<br class=newline>The ideal transformer identity is given by
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>I</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>I</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mi>n</mi>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(12)
</table>
<p>while gyrator identity is given by
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>I</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>I</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mi>n</mi>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(13)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   respectively are the voltage at the input and output of the two-port while
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>I</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>I</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   are the input and output currents. The quantity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>n</mi>
   </math>
   is the turn ratio for a transformer or the gyrator ratio for a gyrator. One can see that for the transformer, the input voltage and current are respectively proportional to the output voltage and current. For the gyrator, the proportionality relation is crossed: the input and output voltages are respectively proportional to the output and input currents. Note that the transformer only works with AC quantities, not DC.<br class=newline>
   <figure class=figure>
<p><img alt="Figure 6. Upper part: transformer symbol, lower part: gyrator symbol." src=${config.ASSETS_URL}/04/02/theory/figure-6.svg><figcaption class=caption>Figure 6. Upper part: transformer symbol, lower part: gyrator symbol.</figcaption></figure>
<p>
   Using the transformer or gyrator identities, an impedance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
      </msub>
   </math>
   at the secondary side can be brought back to the primary side. For the circuits plotted in <a href=# data-img-alt="Figure 7. Ideal transformer and gyrator connected to a load impedance <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>Z</mi></mrow><mrow><mi>l</mi></mrow></msub></math>" data-img-src=${config.ASSETS_URL}/04/02/theory/figure-7.svg>Figure 7</a>, the transformer identity and Ohm’s law give
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>I</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>n</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>I</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <msup>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>l</mi>
               </mrow>
            </msub>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(14)
</table>
<p>while the gyrator identity and Ohm’s law give
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>I</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>n</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <msub>
                     <mrow>
                        <mi>I</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <msup>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-bin>∕</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>l</mi>
               </mrow>
            </msub>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(15)
</table>
<p>
   The respective equivalent circuits are shown on the right side of <a href=# data-img-alt="Figure 7. Ideal transformer and gyrator connected to a load impedance <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>Z</mi></mrow><mrow><mi>l</mi></mrow></msub></math>" data-img-src=${config.ASSETS_URL}/04/02/theory/figure-7.svg>Figure 7</a>. Note that with the gyrator not only is the factor
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>n</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>
   applied but the impedance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
      </msub>
   </math>
   is transformed into an admittance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Y</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
      </msub>
   </math>.
   <figure class=figure>
<p><img alt="Figure 7. Ideal transformer and gyrator connected to a load impedance <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>Z</mi></mrow><mrow><mi>l</mi></mrow></msub></math>" src=${config.ASSETS_URL}/04/02/theory/figure-7.svg><figcaption class=caption>Figure 7. Ideal transformer and gyrator connected to a load impedance <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>Z</mi></mrow><mrow><mi>l</mi></mrow></msub></math>.</figcaption></figure>
<h3 class=likesectionHead>Network analysis</h3>
<p>
   When dealing with a circuit with a large numbers of components, the voltage across the impedances or the current flowing through them can be difficult to calculate. Fortunately, network analysis can be performed by using simple tools such as Kirchhoff’s laws or voltage/current dividers.<br class=newline>Kirchhoff’s current law state that the algebraic sum of currents meeting at a node is null:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi class=MathClass-op>∑</mi>
            <mo>⁡</mo>
         </mrow>
         <mrow>
            <mi>n</mi>
            <mo class=MathClass-rel>=</mo>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mi>N</mi>
         </mrow>
      </msubsup>
      <msub>
         <mrow>
            <mi>I</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   (see <a href=# data-img-alt="Figure 8. Currents flowing into a node." data-img-src=${config.ASSETS_URL}/04/02/theory/figure-8.svg>Figure 8</a>). For a network loop, Kirchhoff’s voltage law states that the sum of all the algebraic voltages across its branches is equal to zero:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi class=MathClass-op>∑</mi>
            <mo>⁡</mo>
         </mrow>
         <mrow>
            <mi>n</mi>
            <mo class=MathClass-rel>=</mo>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mi>N</mi>
         </mrow>
      </msubsup>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   (see <a href=# data-img-alt="Figure 9. Circuit loop." data-img-src=${config.ASSETS_URL}/04/02/theory/figure-9.svg>Figure 9</a>).
   <figure class=figure>
<p><img alt="Figure 8. Currents flowing into a node." src=${config.ASSETS_URL}/04/02/theory/figure-8.svg><figcaption class=caption>Figure 8. Currents flowing into a node.</figcaption></figure><figure class=figure><p><img alt="Figure 9. Circuit loop." src=${config.ASSETS_URL}/04/02/theory/figure-9.svg><figcaption class=caption>Figure 9. Circuit loop.</figcaption></figure>
<p>
   Voltage divider relations are also very handy. For the network plotted on <a href=# data-img-alt="Figure 10. Voltage divider." data-img-src=${config.ASSETS_URL}/04/02/theory/figure-10.svg>Figure 10</a>, the expressions of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   voltages as a function of the source voltage
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>U</mi>
   </math>
   can easily be calculated from
<table class=equation>
   <tr>
      <td style="font-size: 12px;">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mi>U</mi>
            <mstyle class=text>
               <mtext> and </mtext>
            </mstyle>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mi>U</mi>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(16)
</table>
<figure class=figure>
   <p>
      <img alt="Figure 10. Voltage divider." src=${config.ASSETS_URL}/04/02/theory/figure-10.svg>
      <figcaption class=caption>Figure 10. Voltage divider.</figcaption>
</figure>
<p>
   For the current divider of <a href=# data-img-alt="Figure 11. Current divider." data-img-src=${config.ASSETS_URL}/04/02/theory/figure-11.svg>Figure 11</a>, the expressions of currents
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>I</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>I</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   flowing in the two branches can be calculated from the source current
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>I</mi>
   </math>
   using the following equation
<table class=equation>
   <tr>
      <td style="font-size: 12px;">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>I</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mi>I</mi>
            <mstyle class=text>
               <mtext> and </mtext>
            </mstyle>
            <msub>
               <mrow>
                  <mi>I</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mi>I</mi>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(17)
</table>
<figure class=figure>
   <p>
      <img alt="Figure 11. Current divider." src=${config.ASSETS_URL}/04/02/theory/figure-11.svg>
      <figcaption class=caption>Figure 11. Current divider.</figcaption>
</figure>
<h3 class=likesectionHead>Conclusion</h3>
<p>The small number of electrical components and laws presented in this Lesson will be sufficient to understand and model the behaviour of the audio transducers studied in this electroacoustic course. The next Lesson will be devoted to a review on mechanics.
<h3 class=likesectionHead>Glossary</h3>
<p>
<div class=supertabular>
    <dl>
        <dt>AC</dt>
        <dd>Alternating Current.</dd>
        <dt>DC</dt>
        <dd>Direct Current.</dd>
        <dt>RMS</dt>
        <dd>Root Mean Square.</dd>
    </dl>
</div>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt id=X0-nilsson2020electric class=thebibliography>[1]
   <dd id=bib-1 class=thebibliography>
      <p>James William Nilsson and Susan A Riedel. <span class=cmti-10>Electric circuits</span>. Pearson Education Limited, 2020. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9780136114994.
   <dt id=X0-sadiku2016fundamentals class=thebibliography>[2]
   <dd id=bib-2 class=thebibliography>
      <p>Matthew NO Sadiku and Charles K Alexander. <span class=cmti-10>Fundamentals of electric </span><span class=cmti-10>circuits</span>. McGraw-Hill New York, 2016. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9780078028229.
   <dt id=X0-tellegen1948gyrator class=thebibliography>[3]
   <dd id=bib-3 class=thebibliography>
      <p>Bernard DH Tellegen. “The gyrator, a new electric network element”. In: <span class=cmti-10>Philips Res. Rep </span>3.2 (1948), pp. 81–101.
</dl>
`;

export default theory;
